import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Luggage, Plus, MapPin, CalendarDays, Archive, ArchiveRestore, Bell, Trash2, RotateCcw, AlertTriangle, PlaneTakeoff, Merge, Images, GitBranch, Copy, Image as ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ReminderDialog from "./ReminderDialog";
import RemindersPanel from "./RemindersPanel";
import TripImport from "./TripImport";
import PendingImports from "./PendingImports";
import GmailSyncCard from "./GmailSyncCard";
import TripAutoHero from "../TripAutoHero";
import TripCoverSuggestions from "./TripCoverSuggestions";
import TripCardCoverButton from "./TripCardCoverButton";

/* My Trips with lifecycle tabs (owner spec §2, §21):
   Active | Completed | Archived | Reminders — Trash via small link.
   Completed = end date passed but not archived (computed, never automatic). */

export const CountdownRing = ({ startDate, endDate, size = 52 }) => {
  if (!startDate) return null;
  const today = new Date(new Date().toISOString().slice(0, 10));
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : start;
  if (end < today) return null; // past trip
  const inProgress = start <= today && today <= end;
  const days = Math.ceil((start - today) / 86400000);
  const window = 90;
  const pct = inProgress ? 1 : Math.max(0.03, Math.min(1, (window - days) / window));
  const color = inProgress ? "#059669" : days <= 7 ? "#e11d48" : days <= 30 ? "#d97706" : "#1d4ed8";
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="trip-countdown-ring"
      title={inProgress ? "Trip in progress" : `${days} day${days === 1 ? "" : "s"} until departure`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e4e4e7" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="butt"
          style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        {inProgress ? (
          <span className="text-[9px] font-900 tracking-wide" style={{ color }}>NOW</span>
        ) : (
          <>
            <span className="font-mono font-700 text-sm" style={{ color }}>{days}</span>
            <span className="text-[8px] font-mono uppercase text-muted-foreground">day{days === 1 ? "" : "s"}</span>
          </>
        )}
      </div>
    </div>
  );
};

const TABS = ["active", "completed", "archived", "reminders"];
const today = () => new Date().toISOString().slice(0, 10);

export default function TripsSection() {
  const [items, setItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [tab, setTab] = useState("active");
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [reminderFor, setReminderFor] = useState(null); // trip object
  const [merging, setMerging] = useState(false);
  const [sel, setSel] = useState([]); // trip ids selected for combining
  const [albums, setAlbums] = useState({}); // trip_id -> {count, thumbs}

  const load = useCallback(() => {
    api.get("/trips").then((r) => setItems(r.data.items)).catch(() => {});
    api.get("/trips?view=trash").then((r) => setTrash(r.data.items)).catch(() => {});
    api.get("/album/summary/mine").then((r) => setAlbums(r.data.trips || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // §21 UX guard — warn on date typos (Kerala 2926 bug). >5 years out or <1 year past.
  const currentYear = new Date().getFullYear();
  const yearOf = (d) => (d ? parseInt(String(d).slice(0, 4), 10) : NaN);
  const yearWarning = (() => {
    const ys = yearOf(start), ye = yearOf(end);
    for (const y of [ys, ye]) {
      if (!y || isNaN(y)) continue;
      if (y > currentYear + 5) return `That date is ${y} — did you mean ${currentYear + (y - currentYear) % 100}?`;
      if (y < currentYear - 1) return `That date is ${y} — over a year in the past. Double-check the year.`;
    }
    return null;
  })();

  const create = async () => {
    if (!title.trim()) return;
    if (yearWarning && !window.confirm(`${yearWarning}\n\nSave anyway?`)) return;
    try {
      const { data: t } = await api.post("/trips", { title, start_date: start || null, end_date: end || null });
      // Optional cover photo — one round-trip after create so we can attach it
      if (coverFile && t?.id) {
        try {
          const fd = new FormData();
          fd.append("file", coverFile);
          fd.append("owner_type", "trip");
          fd.append("owner_id", t.id);
          fd.append("category", "cover");
          fd.append("label", "Cover photo");
          const { data: att } = await api.post("/trips/attachments", fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          await api.post("/trips", { id: t.id, cover: att.id });
        } catch (uErr) {
          toast.error(uErr?.response?.data?.detail || "Trip created, but cover photo failed to upload");
        }
      }
      setTitle(""); setStart(""); setEnd(""); setCoverFile(null); setCoverPreview(null); setCreating(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create trip"); }
  };

  const pickCover = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Cover photo exceeds 25 MB"); return; }
    setCoverFile(f);
    try { setCoverPreview(URL.createObjectURL(f)); } catch { setCoverPreview(null); }
  };

  const clearCover = () => {
    setCoverFile(null);
    if (coverPreview) { try { URL.revokeObjectURL(coverPreview); } catch {} }
    setCoverPreview(null);
  };

  const archive = async (t, archiveIt) => {
    if (archiveIt) {
      let cancelReminders = false;
      try {
        const r = await api.get(`/reminders/entity/trip/${t.id}`);
        if (r.data.upcoming_count > 0) {
          cancelReminders = !window.confirm(
            `"${t.title}" has ${r.data.upcoming_count} upcoming reminder${r.data.upcoming_count > 1 ? "s" : ""}.\n\nKeep these reminders active? (Recommended)\n\nOK = keep reminders · Cancel = cancel reminders`);
        } else if (!window.confirm(`Archive "${t.title}"?\nIt will move out of your active trips, but all trip information, attachments and history are preserved.`)) {
          return;
        }
      } catch { /* proceed */ }
      await api.post(`/trips/${t.id}/archive`, { archive: true, cancel_reminders: cancelReminders });
      toast.success("Trip archived");
    } else {
      await api.post(`/trips/${t.id}/archive`, { archive: false });
      toast.success("Restored to active");
    }
    load();
  };

  const toTrash = async (t) => {
    if (!window.confirm(`Move "${t.title}" to Recently Deleted?\nIt stays recoverable for 30 days.`)) return;
    try { await api.delete(`/trips/${t.id}`); toast.success("Moved to Recently Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const restore = async (t) => { try { await api.post(`/trips/${t.id}/restore`); toast.success("Trip restored"); load(); } catch { toast.error("Failed"); } };

  const [copyFor, setCopyFor] = useState(null); // trip being copied
  const [copyDate, setCopyDate] = useState("");

  const duplicateTrip = async () => {
    const t = copyFor;
    try {
      const { data } = await api.post(`/trips/${t.id}/duplicate`, copyDate ? { new_start_date: copyDate } : {});
      const shifted = data.dates_shifted_by_days;
      toast.success(`"${data.trip.title}" created${data.copied_segments ? ` with ${data.copied_segments} legs` : ""}${shifted ? ` — all dates shifted ${shifted > 0 ? "+" : ""}${shifted} days` : ""}. Original untouched.`);
      setCopyFor(null); setCopyDate("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not copy the trip"); }
  };

  const doMerge = async () => {
    if (sel.length < 2) return;
    if (!window.confirm(`Combine ${sel.length} trips into one?\nAll legs, stops, travellers and files merge into the earliest trip. The other trips move to Recently Deleted (recoverable for 30 days).`)) return;
    try {
      const { data } = await api.post("/trips/merge", { trip_ids: sel });
      toast.success(`Combined into "${data.title}" — ${data.segments} legs on one timeline`);
      setMerging(false); setSel([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not combine trips"); }
  };
  const destroy = async (t) => {
    if (!window.confirm(`PERMANENTLY delete "${t.title}"?\nThis cannot be undone — all trip data, files and reminders will be gone forever.`)) return;
    try { await api.delete(`/trips/${t.id}/permanent`); toast.success("Permanently deleted"); load(); } catch { toast.error("Failed"); }
  };

  const own = items.filter((t) => !t.shared);
  const shared = items.filter((t) => t.shared);
  const titleById = Object.fromEntries(own.map((t) => [t.id, t.title]));
  const childrenByParent = own.reduce((m, t) => {
    if (t.parent_trip_id && titleById[t.parent_trip_id]) (m[t.parent_trip_id] = m[t.parent_trip_id] || []).push(t);
    return m;
  }, {});
  const isCompleted = (t) => t.end_date && t.end_date < today() && t.lifecycle !== "archived";
  const byTab = {
    active: own.filter((t) => t.lifecycle !== "archived" && !isCompleted(t)),
    completed: own.filter(isCompleted),
    archived: own.filter((t) => t.lifecycle === "archived"),
  };
  const counts = { active: byTab.active.length, completed: byTab.completed.length, archived: byTab.archived.length };

  return (
    <div data-testid="trips-section">
      <GmailSyncCard onImported={load} />
      <PendingImports onImported={load} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-900 text-[#EA4335] text-2xl flex items-center gap-2"><Luggage className="w-6 h-6" /> My Trips</h2>
        <div className="flex gap-2">
          <button onClick={() => { setMerging((v) => !v); setSel([]); }} data-testid="trip-merge-toggle"
            title="Combine two or more trips into one journey"
            className={`inline-flex items-center gap-1.5 px-3 py-2 border text-sm font-600 ${merging ? "border-sky-600 bg-sky-50 text-sky-800" : "border-sky-400 text-sky-700 hover:bg-sky-50"}`}>
            <Merge className="w-4 h-4" /> Combine
          </button>
          <button onClick={() => setImporting(true)} data-testid="trip-import-toggle"
            title="Import from TripIt, calendar files or itineraries"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-orange-400 text-orange-700 text-sm font-600 hover:bg-orange-50">
            <PlaneTakeoff className="w-4 h-4" /> Import
          </button>
          <button onClick={() => setCreating((v) => !v)} data-testid="trip-create-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-sm font-600 hover:bg-orange-500">
            <Plus className="w-4 h-4" /> New trip
          </button>
        </div>
      </div>

      {importing && <TripImport onClose={() => setImporting(false)} onImported={load} />}

      {merging && (
        <div className="border border-sky-400 bg-sky-50 p-3 mb-4 flex flex-wrap items-center gap-3 text-sm" data-testid="trip-merge-bar">
          <span className="text-sky-900">Tick the trips to combine — legs, stops, dates, travellers &amp; files merge into the earliest trip.</span>
          <button onClick={doMerge} disabled={sel.length < 2} data-testid="trip-merge-confirm"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-sky-700 text-white text-sm font-700 hover:bg-sky-800 disabled:opacity-40">
            <Merge className="w-4 h-4" /> Combine {sel.length || ""} trip{sel.length === 1 ? "" : "s"}
          </button>
          <button onClick={() => { setMerging(false); setSel([]); }} data-testid="trip-merge-cancel"
            className="text-xs text-muted-foreground hover:underline">Cancel</button>
        </div>
      )}

      {creating && (
        <div className="border border-orange-300 bg-orange-50/50 p-4 mb-4 flex flex-wrap gap-2" data-testid="trip-create-form">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Trip title (e.g. Japan 2027)" data-testid="trip-title-input"
            className="flex-1 min-w-[180px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="trip-start-input" className="border border-zinc-300 px-2 py-2 text-sm bg-white" />
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="trip-end-input" className="border border-zinc-300 px-2 py-2 text-sm bg-white" />
          <button onClick={create} data-testid="trip-create-btn" className="px-4 py-2 bg-orange-600 text-white text-sm font-700 hover:bg-orange-500">Create trip</button>
          {yearWarning && (
            <div className="basis-full text-xs text-amber-800 bg-amber-100 border border-amber-300 px-2 py-1.5 rounded flex items-center gap-1.5" data-testid="trip-year-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {yearWarning}
            </div>
          )}
          <div className="basis-full flex items-center gap-3 mt-1" data-testid="trip-cover-row">
            {coverPreview ? (
              <div className="relative">
                <img src={coverPreview} alt="cover preview" data-testid="trip-cover-preview"
                  className="w-16 h-16 rounded object-cover border border-zinc-300" />
                <button onClick={clearCover} data-testid="trip-cover-clear"
                  className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 hover:text-destructive">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="w-16 h-16 rounded border border-dashed border-zinc-400 bg-white grid place-items-center cursor-pointer hover:border-orange-500 hover:bg-orange-50"
                data-testid="trip-cover-picker">
                <ImageIcon className="w-5 h-5 text-zinc-400" />
                <input type="file" accept="image/*" onChange={pickCover} className="sr-only" data-testid="trip-cover-input" />
              </label>
            )}
            <span className="text-xs text-muted-foreground">
              {coverFile ? <>Cover ready · {(coverFile.size / (1024 * 1024)).toFixed(1)} MB — will be attached after Create trip</>
                : "Optional cover photo — gives this trip a hero image on the timeline (JPG/PNG/WebP/HEIC, up to 25 MB)"}
            </span>
          </div>
          {!coverFile && title.trim().length >= 2 && (
            <TripCoverSuggestions
              query={title}
              onPick={(f) => {
                setCoverFile(f);
                try { setCoverPreview(URL.createObjectURL(f)); } catch { setCoverPreview(null); }
              }}
            />
          )}
        </div>
      )}

      {/* Lifecycle tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`trips-tab-${t}`}
            className={`px-3 py-1.5 text-sm border capitalize ${tab === t ? "border-orange-500 bg-orange-50 font-700 text-orange-800" : "border-zinc-300"}`}>
            {t}{t !== "reminders" ? ` (${counts[t]})` : ""}
          </button>
        ))}
        <button onClick={() => setTab("trash")} data-testid="trips-tab-trash"
          className={`ml-auto text-xs inline-flex items-center gap-1 ${tab === "trash" ? "text-rose-700 font-700" : "text-muted-foreground hover:text-rose-700"}`}>
          <Trash2 className="w-3.5 h-3.5" /> Recently Deleted ({trash.length})
        </button>
      </div>

      {tab === "reminders" ? (
        <RemindersPanel />
      ) : tab === "trash" ? (
        trash.length === 0 ? <p className="text-sm text-muted-foreground">Recently Deleted is empty. Items here are removed permanently after 30 days.</p> : (
          <div className="space-y-2" data-testid="trash-list">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Items are permanently removed 30 days after deletion.</p>
            {trash.map((t) => (
              <div key={t.id} className="border border-zinc-300 bg-white p-3 flex flex-wrap items-center gap-3" data-testid={`trash-item-${t.id}`}>
                <span className="font-700 text-sm flex-1">{t.title}</span>
                <span className="text-xs text-muted-foreground">deleted {t.deleted_at ? new Date(t.deleted_at).toLocaleDateString() : ""}</span>
                <button onClick={() => restore(t)} data-testid={`trash-restore-${t.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-emerald-700 hover:underline"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                <button onClick={() => destroy(t)} data-testid={`trash-destroy-${t.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-rose-600 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Delete forever</button>
              </div>
            ))}
          </div>
        )
      ) : byTab[tab].length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tab === "active" ? "No active trips — create your first trip and start planning!" :
            tab === "completed" ? "No completed trips yet — finished trips appear here until you archive them." :
              "Nothing archived. Archiving keeps old trips searchable without cluttering your active list."}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {byTab[tab].map((t) => (
            <div key={t.id} className={`border bg-white p-4 flex flex-col ${merging && sel.includes(t.id) ? "border-sky-500 ring-1 ring-sky-300" : "border-zinc-300"}`} data-testid={`trip-card-${t.id}`}>
              {merging && (
                <label className="flex items-center gap-2 mb-2 text-xs font-700 text-sky-800 cursor-pointer">
                  <input type="checkbox" checked={sel.includes(t.id)} data-testid={`trip-merge-check-${t.id}`}
                    onChange={() => setSel((s) => (s.includes(t.id) ? s.filter((x) => x !== t.id) : [...s, t.id]))} />
                  Select to combine
                </label>
              )}
              {t.cover && !t.shared && (
                <Link to={`/trips/${t.id}`} className="-mx-4 -mt-4 mb-3 block">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/trips/attachments/${t.cover}/image?auth=${localStorage.getItem("token")}`}
                    alt="" data-testid={`trip-cover-${t.id}`} loading="lazy"
                    className="w-full h-32 object-cover border-b border-zinc-200"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                </Link>
              )}
              {!t.cover && !t.shared && (t.destination || t.title) && (
                <Link to={`/trips/${t.id}`} className="-mx-4 -mt-4 mb-3 block" data-testid={`trip-auto-hero-link-${t.id}`}>
                  <TripAutoHero
                    query={t.destination || t.title}
                    alt={t.title}
                    className="w-full h-32 border-b border-zinc-200"
                    testid={`trip-auto-hero-${t.id}`}
                  />
                </Link>
              )}
              <div className="flex items-start justify-between gap-2">
                <Link to={`/trips/${t.id}`} className="font-heading font-700 text-lg hover:text-orange-600 leading-tight">{t.title}</Link>
                {tab === "active" && !t.shared && <CountdownRing startDate={t.start_date} endDate={t.end_date} />}
                {t.lifecycle === "archived" && <span className="text-[10px] font-900 tracking-widest bg-zinc-200 text-zinc-600 px-1.5 py-0.5" data-testid={`archived-badge-${t.id}`}>ARCHIVED</span>}
                {tab === "completed" && <span className="text-[10px] font-900 tracking-widest bg-emerald-100 text-emerald-700 px-1.5 py-0.5">COMPLETED</span>}
              </div>
              {!t.cover && !t.shared && (
                <TripCardCoverButton trip={t} onSaved={load} />
              )}
              {t.parent_trip_id && titleById[t.parent_trip_id] && (
                <Link to={`/trips/${t.parent_trip_id}`} data-testid={`trip-parent-badge-${t.id}`}
                  className="mt-1 inline-flex items-center gap-1 self-start text-[11px] font-700 text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 hover:border-violet-400">
                  <GitBranch className="w-3 h-3" /> Side trip of: {titleById[t.parent_trip_id]}
                </Link>
              )}
              {(t.start_date || t.end_date) && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {t.start_date}{t.end_date ? ` → ${t.end_date}` : ""}</div>
              )}
              {t.destination && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t.destination}</div>}
              {t.next_departure && (
                <div className="text-xs mt-1.5 flex items-center gap-1 text-sky-700 font-700" data-testid={`trip-next-dep-${t.id}`}>
                  <PlaneTakeoff className="w-3.5 h-3.5" />
                  Next: {t.next_departure.mode}{t.next_departure.ref ? ` ${t.next_departure.ref}` : ""} · {t.next_departure.date}{t.next_departure.time ? ` ${t.next_departure.time.slice(0, 5)}` : ""}
                  {t.next_departure.from_place ? ` · ${t.next_departure.from_place}${t.next_departure.to_place ? ` → ${t.next_departure.to_place}` : ""}` : ""}
                </div>
              )}
              {typeof t.completion_pct === "number" && !t.shared && (
                <div className="mt-2" data-testid={`trip-completion-${t.id}`}>
                  <div className="flex justify-between text-[10px] font-mono text-muted-foreground mb-0.5">
                    <span>Trip information</span><span>{t.completion_pct}% complete</span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 border border-zinc-200">
                    <div className={`h-full ${t.completion_pct >= 70 ? "bg-emerald-500" : t.completion_pct >= 40 ? "bg-amber-500" : "bg-zinc-400"}`} style={{ width: `${t.completion_pct}%` }} />
                  </div>
                </div>
              )}
              {(childrenByParent[t.id] || []).length > 0 && (
                <div className="mt-2" data-testid={`trip-side-trips-${t.id}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-violet-600 mb-1 flex items-center gap-1">
                    <GitBranch className="w-3 h-3" /> Side trips ({childrenByParent[t.id].length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {childrenByParent[t.id].map((s) => (
                      <Link key={s.id} to={`/trips/${s.id}`} data-testid={`trip-side-chip-${s.id}`}
                        className="text-[11px] font-700 text-violet-800 bg-violet-50 border border-violet-200 px-2 py-0.5 hover:border-violet-500 transition-colors">
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {albums[t.id]?.count > 0 && (
                <Link to={`/trips/${t.id}#photos`} className="mt-2 block" data-testid={`trip-photos-strip-${t.id}`}
                  title={`Open the ${albums[t.id].count} photos of this trip`}>
                  <div className="flex items-center gap-1.5">
                    {albums[t.id].thumbs.map((u) => (
                      <img key={u} src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" loading="lazy"
                        className="w-12 h-12 object-cover border border-zinc-200" />
                    ))}
                    <span className="inline-flex items-center gap-1 text-xs font-700 text-emerald-700 ml-1">
                      <Images className="w-3.5 h-3.5" /> {albums[t.id].count} photo{albums[t.id].count !== 1 ? "s" : ""}
                    </span>
                  </div>
                </Link>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-3 pt-2 border-t border-zinc-100 text-xs">
                <Link to={`/trips/${t.id}`} className="font-700 text-orange-700 hover:underline">Open</Link>
                {t.start_date && t.end_date && t.start_date <= today() && today() <= t.end_date && (
                  <Link to={`/travel-day/${t.id}`} data-testid={`trip-travel-day-${t.id}`}
                    className="inline-flex items-center gap-1 font-700 text-amber-700 hover:underline">
                    <PlaneTakeoff className="w-3.5 h-3.5" /> Travel Day
                  </Link>
                )}
                <button onClick={() => setReminderFor(t)} data-testid={`trip-remind-${t.id}`} className="inline-flex items-center gap-1 font-700 text-indigo-700 hover:underline"><Bell className="w-3.5 h-3.5" /> Remind</button>
                <button onClick={() => { setCopyFor(t); setCopyDate(""); }} data-testid={`trip-duplicate-${t.id}`} className="inline-flex items-center gap-1 font-700 text-sky-700 hover:underline"><Copy className="w-3.5 h-3.5" /> Copy</button>
                {t.lifecycle === "archived" ? (
                  <button onClick={() => archive(t, false)} data-testid={`trip-unarchive-${t.id}`} className="inline-flex items-center gap-1 font-700 text-emerald-700 hover:underline"><ArchiveRestore className="w-3.5 h-3.5" /> Restore</button>
                ) : (
                  <button onClick={() => archive(t, true)} data-testid={`trip-archive-${t.id}`} className="inline-flex items-center gap-1 font-700 text-zinc-600 hover:underline"><Archive className="w-3.5 h-3.5" /> Archive</button>
                )}
                <button onClick={() => toTrash(t)} data-testid={`trip-trash-${t.id}`} className="inline-flex items-center gap-1 font-700 text-rose-600 hover:underline ml-auto"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "active" && shared.length > 0 && (
        <div className="mt-6" data-testid="shared-trips">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Shared with me</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {shared.map((t) => (
              <Link key={t.id} to={`/trips/${t.id}`} className="border border-sky-300 bg-sky-50/40 p-4 hover:border-sky-500" data-testid={`shared-trip-${t.id}`}>
                <div className="font-heading font-700">{t.title}</div>
                <div className="text-xs text-sky-700 mt-1">Shared by {t.owner_name} · {t.my_role === "edit" ? "can edit" : t.my_role === "finance" ? "financial manager" : "view only"}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {copyFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="copy-trip-modal">
          <div className="bg-white border border-zinc-300 p-5 w-full max-w-sm">
            <h3 className="font-heading font-900 text-lg mb-1">Copy "{copyFor.title}"</h3>
            <p className="text-xs text-muted-foreground mb-3">Pick a new start date to shift every trip and segment date by the same amount — or leave empty to keep the original dates. The original trip stays untouched.</p>
            <label className="block mb-4">
              <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">New start date (optional)</span>
              <input type="date" value={copyDate} onChange={(e) => setCopyDate(e.target.value)} data-testid="copy-trip-date"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-sky-600" />
            </label>
            <div className="flex gap-2">
              <button onClick={duplicateTrip} data-testid="copy-trip-confirm"
                className="flex-1 bg-sky-700 text-white text-sm font-700 py-2 hover:bg-sky-600">Copy trip</button>
              <button onClick={() => setCopyFor(null)} data-testid="copy-trip-cancel"
                className="px-4 border border-zinc-300 text-sm font-700 hover:bg-zinc-50">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {reminderFor && (
        <ReminderDialog entityType="trip" entityId={reminderFor.id} tripId={reminderFor.id}
          defaultTitle={reminderFor.title} refDate={reminderFor.start_date}
          onClose={() => setReminderFor(null)} onSaved={() => {}} />
      )}
    </div>
  );
}
